import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DoorOpen } from "lucide-react";
import { toast } from "sonner";

import { officeService } from "../api";
import { useOfficeRealtime } from "../realtime/OfficeRealtimeProvider";
import { Avatar } from "./Avatar";

interface EntryRequest {
  request_id: string;
  zone_id: string;
  zone_name: string;
  from: { user_id: string; name: string; avatar_url?: string };
  expires_at: string;
}

/**
 * "Somebody is at the door."
 *
 * Shown to whoever is inside an occupied private meeting room. An empty private
 * room is free to walk into — the door only matters once somebody is in there
 * (FR-2.6), so this only ever appears to occupants.
 *
 * Withdrawn when another occupant answers first, or when it expires, so two
 * people in a room do not both have to deal with it.
 */
export function RoomEntryPrompts() {
  const { t } = useTranslation();
  const { subscribe } = useOfficeRealtime();
  const [requests, setRequests] = useState<EntryRequest[]>([]);
  const [answering, setAnswering] = useState(false);

  useEffect(
    () =>
      subscribe((frame) => {
        if (frame.type === "room.entry_request") {
          window.dispatchEvent(new CustomEvent("weco:office:attention"));
          const request = frame.payload as unknown as EntryRequest;
          setRequests((prev) =>
            prev.some((r) => r.request_id === request.request_id) ? prev : [request, ...prev],
          );
        }
        if (frame.type === "room.entry_withdrawn") {
          const id = (frame.payload as { request_id: string }).request_id;
          setRequests((prev) => prev.filter((r) => r.request_id !== id));
        }
      }),
    [subscribe],
  );

  const current = requests[0];
  if (!current) return null;

  const respond = async (accepted: boolean) => {
    setAnswering(true);
    try {
      await officeService.answerRoomEntry(current.request_id, accepted);
      setRequests((prev) => prev.filter((r) => r.request_id !== current.request_id));
    } catch (error) {
      setRequests((prev) => prev.filter((r) => r.request_id !== current.request_id));
      toast.error(
        error instanceof Error ? error.message : t("homework.room.requestGone"),
      );
    } finally {
      setAnswering(false);
    }
  };

  return (
    <div
      role="alertdialog"
      aria-labelledby="room-entry-title"
      className="fixed bottom-6 start-6 z-[120] w-80 rounded-2xl border border-border bg-background p-4 shadow-2xl"
    >
      <div className="mb-3 flex items-center gap-3">
        <Avatar
          name={current.from.name}
          id={current.from.user_id}
          url={current.from.avatar_url}
          size={40}
        />
        <div className="min-w-0">
          <p id="room-entry-title" className="flex items-center gap-1.5 text-[14px] font-semibold">
            <DoorOpen className="size-4 text-primary" />
            {t("homework.room.atTheDoor", { name: current.from.name })}
          </p>
          <p className="truncate text-[12px] text-muted-foreground">{current.zone_name}</p>
        </div>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={answering}
          onClick={() => void respond(true)}
          className="flex-1 rounded-lg bg-primary px-3 py-2 text-[13px] font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {t("homework.knock.letIn")}
        </button>
        <button
          type="button"
          disabled={answering}
          onClick={() => void respond(false)}
          className="flex-1 rounded-lg border border-border px-3 py-2 text-[13px] font-semibold transition-colors hover:bg-muted disabled:opacity-50"
        >
          {t("homework.knock.notNow")}
        </button>
      </div>
      {requests.length > 1 && (
        <p className="mt-2 text-center text-[11px] text-muted-foreground">
          {t("homework.knock.moreWaiting", { count: requests.length - 1 })}
        </p>
      )}
    </div>
  );
}
